/**
 * Every place in the app a coach mark can point at.
 *
 * An element becomes an anchor by spreading `coachAnchor(id, scope?)` onto it,
 * which writes `data-coach` (and `data-coach-scope` for anchors that repeat,
 * such as one Record button per Content Project). Journeys refer to anchors by
 * id only, so redesigning a surface never breaks a journey as long as the new
 * markup carries the same anchor. A web test fails if an anchor listed here is
 * missing from the source, or if the source uses one that is not listed.
 */

export const COACH_SURFACES = {
  'boards-home': 'Boards home',
  toolbar: 'Board toolbar',
  'right-toolbar': 'Right toolbar',
  copilot: 'Copilot',
  'side-panel': 'Side panel',
  chat: 'Chat',
  canvas: 'Canvas nodes',
  studio: 'Content studio',
} as const;

export type CoachSurface = keyof typeof COACH_SURFACES;

export type CoachAnchorScope = 'chatNodeId' | 'toolCallId' | 'projectNodeId';

export interface CoachAnchorDefinition {
  surface: CoachSurface;
  label: string;
  /** Set when the anchor repeats; the element carries this id as `data-coach-scope`. */
  scope?: CoachAnchorScope;
}

export const COACH_ANCHORS = {
  'boards.sidebar': { surface: 'boards-home', label: 'Boards sidebar' },
  'board.toolbar': { surface: 'toolbar', label: 'Board toolbar' },
  'toolbar.ai-chat': { surface: 'toolbar', label: 'AI Chat button' },
  'toolbar.record': { surface: 'toolbar', label: 'Record audio button' },
  'toolbar.upload': { surface: 'toolbar', label: 'Upload button' },
  'board.right-toolbar': { surface: 'right-toolbar', label: 'Right toolbar' },
  'toolbar.copilot': { surface: 'right-toolbar', label: 'Copilot button' },
  'copilot.input': { surface: 'copilot', label: 'Copilot message box' },
  'copilot.mic': { surface: 'copilot', label: 'Microphone in the Copilot message box' },
  'copilot.upload': { surface: 'copilot', label: 'Upload button in the Copilot message box' },
  'copilot.connect': {
    surface: 'copilot',
    label: 'Connect-accounts button in the Copilot message box',
  },
  'copilot.panel': { surface: 'copilot', label: 'Copilot conversation' },
  'coach.question': { surface: 'copilot', label: 'The tour’s card in the Copilot conversation' },
  'copilot.attach': { surface: 'copilot', label: 'Add to this message' },
  'copilot.history': { surface: 'copilot', label: 'Your chats' },
  'copilot.apps': { surface: 'copilot', label: 'Your integrations' },
  'board.side-panel': { surface: 'side-panel', label: 'Side panel' },
  'chat.input': { surface: 'chat', label: 'Chat message box', scope: 'chatNodeId' },
  'chat.brand': { surface: 'chat', label: 'Brand picker in a chat', scope: 'chatNodeId' },
  'chat.hooks': {
    surface: 'chat',
    label: 'The clickable hooks or titles in a chat reply',
    scope: 'chatNodeId',
  },
  'chat.script-card.add-to-board': {
    surface: 'chat',
    label: 'Add to board on a script card',
    scope: 'toolCallId',
  },
  'project.open-studio': {
    surface: 'canvas',
    label: 'Open a post',
    scope: 'projectNodeId',
  },
  'studio.media': { surface: 'studio', label: 'Post media and script' },
  'studio.record': { surface: 'studio', label: 'Record this post' },
  'studio.caption': { surface: 'studio', label: 'Post caption' },
  'studio.edit-media': { surface: 'studio', label: 'Edit post media' },
  'studio.back-to-post': { surface: 'studio', label: 'Return from the media editor' },
  'studio.delivery': { surface: 'studio', label: 'Inline posting and planning controls' },
  'studio.recording-entry': { surface: 'studio', label: 'Camera choice' },
  'studio.timeline': { surface: 'studio', label: 'Video timeline' },
  'studio.captions': { surface: 'studio', label: 'Captions controls' },
  'studio.post': { surface: 'studio', label: 'Post button' },
  'studio.done-editing': { surface: 'studio', label: 'Done editing' },
} as const satisfies Record<string, CoachAnchorDefinition>;

export type CoachAnchorId = keyof typeof COACH_ANCHORS;

export const COACH_ANCHOR_IDS = Object.keys(COACH_ANCHORS) as CoachAnchorId[];

type ScopedAnchorId = {
  [K in CoachAnchorId]: (typeof COACH_ANCHORS)[K] extends { scope: CoachAnchorScope } ? K : never;
}[CoachAnchorId];
type UnscopedAnchorId = Exclude<CoachAnchorId, ScopedAnchorId>;

export interface CoachAnchorAttributes {
  'data-coach': CoachAnchorId;
  'data-coach-scope'?: string;
}

/** Attributes that make an element a coach anchor. Scoped anchors require their scope id. */
export function coachAnchor(id: UnscopedAnchorId): CoachAnchorAttributes;
export function coachAnchor(id: ScopedAnchorId, scope: string): CoachAnchorAttributes;
export function coachAnchor(id: CoachAnchorId, scope?: string): CoachAnchorAttributes {
  return scope ? { 'data-coach': id, 'data-coach-scope': scope } : { 'data-coach': id };
}

/** CSS selector for an anchor; with a scope value, only that instance. */
export function coachAnchorSelector(id: CoachAnchorId, scope?: string): string {
  const base = `[data-coach="${id}"]`;
  return scope ? `${base}[data-coach-scope="${escapeAttribute(scope)}"]` : base;
}

/** CSS selector for a node on the board (React Flow marks each with its id). */
export function coachNodeSelector(nodeId: string): string {
  return `.react-flow__node[data-id="${escapeAttribute(nodeId)}"]`;
}

function escapeAttribute(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
